"use client";

import { animated, useTransition } from "@react-spring/web";
import Image, { StaticImageData } from "next/image";
import { useRef, useState } from "react";

import dots from "@/public/home/geometry.png";
import { sleep } from "@/utils/sleep";

export const Carousel = ({
  slides,
}: {
  slides: (StaticImageData | string)[];
}) => {
  const [index, setIndex] = useState(0);
  const initialRender = useRef(true);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const AnimatedImage = animated(Image as any);

  const transitions = useTransition(index, {
    key: index,
    from: {
      opacity: initialRender.current ? 0.99 : 0,
      transform: initialRender.current ? "scale(1)" : "scale(1.05)",
    },
    enter: { opacity: 1, transform: "scale(1)" },
    leave: { opacity: 0, transform: "scale(1.05)" },
    config: { duration: 800 },
    onRest: async (_a, _b, currentIndex) => {
      await sleep(2000);
      if (initialRender.current) initialRender.current = false;

      if (index === currentIndex)
        setIndex((index) => (index + 1) % slides.length);
    },
  });

  return (
    <div className="relative flex w-full items-center justify-center py-16 pr-3 lg:justify-end lg:py-0 lg:pr-6">
      <div className="relative flex aspect-1 w-full max-w-[300px] select-none items-center lg:max-w-[400px] after:lg:-right-6 after:lg:-top-6 2xl:max-w-[500px]">
        <Image
          src={dots}
          alt="green-dots"
          className="absolute -bottom-12 -left-14 scale-75 sm:scale-90 2xl:scale-100"
        />
        <div className="absolute -right-[10%] -top-[10%] h-[80%] w-[70%] bg-[linear-gradient(90deg,#14B3BC_0%,#8EDC99_100%)]" />
        <div className="absolute h-full w-full overflow-hidden shadow-xl">
          {transitions((style, i) => (
            <AnimatedImage
              priority
              src={slides[i]}
              alt={`slide-${i}`}
              width={300}
              height={300}
              placeholder="blur"
              style={style}
              className="unselectable absolute h-full w-full"
              aria-label="intro carousel"
            />
          ))}
        </div>
      </div>
    </div>
  );
};
